import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { useHistory } from "react-router-dom";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import {
  getCurrentPrice,
  getCurrentVatPercentage,
  getCurrentVatValue
} from "src/core/products/utils";
import {
  CreateListFromOrderParams,
  listsApi,
  IList
} from "src/core/lists/api/lists.api";
import {
  CancelOrderParams,
  CreateOrderParams,
  ordersApi,
  SubmitOrderParams,
  UpdateOrderParams,
  IOrder,
  DuplicateOrderParams,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import { orderInitialState } from "src/core/orders/store/orders.reducer";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import {
  useCanSaveOrder,
  useDaysToCutoff
} from "src/core/orders/hooks/validation";
import { useDrop } from "src/core/drops/hooks";
import { IDrop } from "src/core/drops/api/drops.api";
import { IProduct } from "src/core/products/api/products.api";
import { useConnectorApi } from "src/modules/connector/api/connector.api";
import { tokenizeString } from "src/library/utils";
import { IApiResponse } from "src/core/shared/hooks/useAxios";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import { useProductLatestPackSize } from "src/core/products/hooks/useProductLatestPackSize";

import {
  ICurrentOrder,
  OrderMode,
  IOrderLine
} from "../api/orders.api";
import useSetCurrentOrder from "./useSetCurrentOrder";
import { orderPlacedRedirectPath } from "../utils/order-placed-redirect";

export interface IOrderState {
  currentOrder: ICurrentOrder;
  isCreatingOrder: boolean;
  isSubmittingOrder: boolean;
  isSavingOrder: boolean;
  isLoading: boolean;
  drop: IDrop | null;
  cancelOrderResponse: IApiResponse<IOrder, null>
}

export interface IOrderActions {
  createOrder: (date: Date | null, origin: OrderOrigin, products?: IProduct[], dropOverride?: IDrop | null) => void;
  cancelOrder: (id: number) => void;
  setOrderMode: (mode: OrderMode, newOrder?: IOrder) => boolean;
  submitOrder: (date?: Date) => void;
  resyncOrder: () => void;
  refreshOrder: () => void;
  saveOrder: (andCheckout?: boolean) => void;
  markAsManuallyProcessed: (newOrderUpdates: UpdateOrderParams) => void;
  createListFromOrder: (params: CreateListFromOrderParams) => void;
  setOrder: (newCurrentOrder: Omit<ICurrentOrder, "allowedDays">) => void;
  duplicateOrder: (deliveryDate: Date, editBeforePlacing: boolean, customerReference?: string) => void;
}

/**
 * useOrder hook enables managed access to core order interactions including utilising
 * state management either locally or via redux and accessing orders api, keeping state
 * updated.
 *
 * @param orderId - used when working with state from redux
 * @param order - don't use redux, use manually edited order with same access to useOrder actions
 * @param getData - master data, responsible for getting data from database
 * @param refetchOrders - callback used to refresh orders
 * @returns
 */
const useOrder = (orderId?: number | null, order?: IOrder | null, getData = false, refetchOrders?: () => void): [IOrderState, IOrderActions] => {
  const dispatch = useDispatch();
  const history = useHistory();
  const setCurrentOrder = useSetCurrentOrder();

  const {
    orderStatuses, currentOrder, installedModules, global: {
      settings, role, masqueradeMode
    }
  } = useMappedState(({ global, orders }: IStore) => ({
    orderStatuses: global.settings ? global.settings.orderStatuses : [],
    installedModules: global.settings ? global.settings.installedModules : [],
    currentOrder: orders.currentOrder,
    global
  }));

  const { minimumLeadTime } = useMinimumLeadTime();
  const preauthEnabled = !!settings?.payment?.preauthorizationsEnabled;
  const canSaveOrder = useCanSaveOrder();
  const daysToCutOff = useDaysToCutoff();
  const [ getOrderResponse, getOrder ] = ordersApi<IOrder>("GET_ORDER");
  const [ createOrderResponse, createOrderRequest ] = ordersApi<IOrder>("CREATE_ORDER");
  const [ submitOrderResponse, submitOrderRequest ] = ordersApi<IOrder>("SUBMIT_ORDER");
  const [ saveOrderResponse, saveOrderRequest ] = ordersApi<IOrder>("UPDATE_ORDER");
  const [ cancelOrderResponse, cancelOrderRequest ] = ordersApi<IOrder>("CANCEL_ORDER");
  const [ importRJLOrderRes, importRJLOrderReq ] = useConnectorApi("IMPORT_API_ORDER");
  const [ createListFromOrderResponse, createListFromOrder ] = listsApi<IList>("CREATE_LIST_FROM_ORDER");
  const [ duplicateOrderRes, duplicateOrderReq ] = ordersApi<IOrder, DuplicateOrderParams>("DUPLICATE_ORDER");
  const [ , { getPackSizeByProduct } ] = useProductLatestPackSize();

  // order state managed seperate from redux
  const [ localOrderState, setLocalOrderState ] = React.useState<ICurrentOrder>({
    mode: "view",
    data: order || null,
    allowedDays: null,
    saved: true
  });

  // order state used within this hook based on
  // whether we're working with a local order or redux state
  const orderToUse = order ? localOrderState : currentOrder;
  const [ { drop } ] = useDrop();

  /**
   * Reloads the order
   */
  const triggerReload = React.useCallback(() => {
    if (refetchOrders) {
      refetchOrders();
    }
  }, [ refetchOrders ]);

  const allowedDays = React.useMemo(() => {
    let days = null;

    if (orderToUse.data) {
      if (drop && drop.groupedSettings && drop.groupedSettings.delivery_week_leadtimes && orderToUse.data.orderDeliveryDate) {
        const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
        const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

        if (leadTimes && defaultCutoff && minimumLeadTime) {
          days = daysToCutOff(orderToUse.data.orderDeliveryDate, minimumLeadTime);
        }
      }
    }

    return days;
  }, [
    orderToUse.data,
    daysToCutOff,
    drop,
    minimumLeadTime
  ]);

  // fetch the order on mount if in view mode or if the id changed for some reason
  React.useEffect(() => {
    if (orderId && getData) {
      if ((orderToUse.data && orderToUse.data.id !== orderId) || !orderToUse.data || orderToUse.mode === "view") {
        getOrder<number>(orderId);
      }
    }
  }, [ orderId, getData ]);

  React.useEffect(() => {
    if (createListFromOrderResponse.data) {
      toast.success(`${_.capitalize(settings?.terminology ? tokenizeString("%LIST%", settings.terminology) : "list")} created succesfully`);
      history.push(`/lists/${createListFromOrderResponse.data.id}`);
    }

    if (createListFromOrderResponse.error) {
      toast.error(createListFromOrderResponse.error.message);
    }
  }, [
    createListFromOrderResponse,
    history,
    settings
  ]);

  const cancelOrder = (id: number) => {
    cancelOrderRequest<CancelOrderParams>(id);
  };

  // manages how the state of the order is updated based on whether we're working with local or redux
  const setOrder = React.useCallback(
    (newCurrentOrder: Omit<ICurrentOrder, "allowedDays">) => {
      if (!orderId || (newCurrentOrder.data && newCurrentOrder.data.id === orderId)) {
        if (!newCurrentOrder.data) {
          if (order) {
            setLocalOrderState({
              ...newCurrentOrder,
              allowedDays
            });
          } else {
            setCurrentOrder({
              ...newCurrentOrder,
              allowedDays
            });
          }

          return;
        }

        let newAllowedDays = null;

        if (drop && drop.groupedSettings && drop.groupedSettings.delivery_week_leadtimes && newCurrentOrder.data.orderDeliveryDate) {
          const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
          const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);

          if (leadTimes && defaultCutoff && minimumLeadTime) {
            newAllowedDays = daysToCutOff(newCurrentOrder.data.orderDeliveryDate, minimumLeadTime);
          }
        }

        if (order) {
          setLocalOrderState({
            ...newCurrentOrder,
            allowedDays: newAllowedDays || allowedDays
          });
        } else {
          setCurrentOrder({
            ...newCurrentOrder,
            allowedDays: newAllowedDays || allowedDays
          });
        }
      }
    },
    [
      allowedDays,
      daysToCutOff,
      drop,
      order,
      orderId,
      setCurrentOrder,
      minimumLeadTime
    ]
  );

  /**
   * every time the order is passed thorugh from above set it here
   */
  React.useEffect(() => {
    if (order) {
      setOrder({
        mode: "view",
        data: order,
        saved: true
      });
    }
  }, [ order, setOrder ]);

  // Handle the cancel response
  React.useEffect(() => {
    if (cancelOrderResponse.data) {
      if (order) {
        triggerReload();
      } else if (orderToUse.data) {
        setOrder({
          ...orderToUse,
          data: {
            ...orderToUse.data,
            orderStatus: _.find(orderStatuses, status => status.code === "cancelled"),
            orderStatusId: cancelOrderResponse.data.orderStatusId
          },
          mode: "view"
        });
      }

      cancelOrderResponse.data = null;
    }

    if (cancelOrderResponse.error) {
      toast.error(`Could not cancel. ${cancelOrderResponse.error.message}`);
      cancelOrderResponse.error = null;
    }
  }, [
    cancelOrderResponse,
    orderToUse,
    order,
    orderStatuses,
    setOrder,
    triggerReload
  ]);

  React.useEffect(() => {
    if (getOrderResponse.data) {
      if (masqueradeMode?.active && drop?.id !== getOrderResponse.data.branchId) {
        history.push("/dashboard");

        return;
      }

      if (orderToUse.data && orderToUse.data.id === getOrderResponse.data.id) {
        setOrder({
          ...orderToUse,
          data: {
            ...orderToUse.data,
            ...getOrderResponse.data
          },
          saved: true
        });
      } else {
        setOrder({
          data: getOrderResponse.data,
          mode: "view",
          saved: true
        });
      }

      getOrderResponse.data = null;
    }

    if (getOrderResponse.error) {
      setOrder(orderInitialState.currentOrder);
      dispatch(setGlobalError(getOrderResponse.error));
    }
  }, [
    orderToUse,
    dispatch,
    getOrderResponse,
    drop,
    masqueradeMode,
    history,
    setOrder
  ]);

  React.useEffect(() => {
    if (createOrderResponse.data) {
      setOrder({
        data: createOrderResponse.data,
        mode: "edit",
        saved: true
      });

      if (role?.code === "van-sales-admin") {
        history.push(`/orders/${createOrderResponse.data.id}`);
      } else {
        history.push("/products");
      }

      createOrderResponse.data = null;
    }

    if (createOrderResponse.error) {
      toast.error(createOrderResponse.error.message || "There was an error whilst creating your order. Please try again.");
    }
  }, [
    createOrderResponse,
    history,
    role?.code,
    setOrder
  ]);

  const createOrder = React.useCallback(
    (deliveryDate: Date | null, origin: OrderOrigin, products?: IProduct[], dropOverride?: IDrop | null) => {
      const dropToUse = dropOverride || drop;
      const orderStatusId = getOrderStatusIdFromCode(orderStatuses, "draft");

      const orderLines: IOrderLine[] = _.map(products, product => ({
        productId: product.id,
        quantity: (getPackSizeByProduct ? getPackSizeByProduct(product || null) || { quantity: 1 } : { quantity: 1 }).quantity,
        price: getCurrentPrice(product, installedModules, 1),
        product,
        vatPercentage: getCurrentVatPercentage(product),
        totalVatValue: getCurrentVatValue(product)
      }));

      if (orderStatusId && dropToUse) {
        createOrderRequest<CreateOrderParams>({
          branchId: dropToUse.id,
          orderStatusId,
          orderDeliveryDate: deliveryDate,
          orderLines,
          type: "adhoc",
          origin
        });
      }
    },
    [
      drop,
      getPackSizeByProduct,
      installedModules,
      orderStatuses
    ]
  );

  const refreshOrder = React.useCallback(() => {
    if (orderToUse.data) {
      getOrder<number>(orderToUse.data.id);
    }
  }, [ orderToUse.data, getOrder ]);

  const setOrderMode = React.useCallback(
    (mode: OrderMode, newOrder?: IOrder): boolean => {
      if (newOrder) {
        setOrder({
          data: newOrder,
          mode,
          saved: true
        });

        return true;
      }

      if (orderToUse.data) {
        setOrder({
          data: orderToUse.data,
          mode,
          saved: orderToUse.saved
        });

        return true;
      } else if (order) {
        setOrder({
          data: order,
          mode,
          saved: false
        });
      }

      return false;
    },
    [
      orderToUse.data,
      orderToUse.saved,
      order,
      setOrder
    ]
  );

  const saveOrder = React.useCallback(
    () => {
      if (orderToUse.data && orderToUse.saved) {
        if (canSaveOrder(orderToUse.data) === null) {
          saveOrderRequest<UpdateOrderParams>(orderToUse.data);
        }
      }
    },
    [
      orderToUse.data,
      orderToUse.saved,
      canSaveOrder
    ]
  );

  const markAsManuallyProcessed = React.useCallback(
    (newOrderUpdates: UpdateOrderParams) => {
      // Check that updates exists
      if (Object.keys(newOrderUpdates).length > 0) {
        saveOrderRequest<UpdateOrderParams>(newOrderUpdates);
      }
    },
    [ ]
  );

  React.useEffect(() => {
    if (saveOrderResponse.error) {
      setOrderMode("edit");
      toast.error(`There was an error whilst saving your order - ${saveOrderResponse.error.message}`);
      saveOrderResponse.error = null;
    } else if (saveOrderResponse.data && orderToUse.data) {
      const updatedOrder = {
        ...saveOrderResponse.data,
        orderLines: orderToUse.data.orderLines
      };

      if (order) {
        triggerReload();
      }

      toast.success("Order updated successfully.");

      setOrder({
        data: updatedOrder,
        mode: orderToUse.mode,
        saved: true
      });
      saveOrderResponse.data = null;
    }
  }, [
    orderToUse.data,
    orderToUse.mode,
    order,
    saveOrderResponse,
    setOrder,
    setOrderMode,
    triggerReload
  ]);

  const submitOrder = React.useCallback((date?: Date) => {
    // dont send any of the model association data as its bulky and uneeded
    if (orderToUse.data) {
      submitOrderRequest<SubmitOrderParams>({
        ...orderToUse.data,
        orderDeliveryDate: date ? date : orderToUse.data.orderDeliveryDate,
        erpSyncStatus: undefined,
        list: undefined,
        orderNotes: undefined,
        orderStatus: undefined,
        transactions: undefined,
        branch: undefined,
        errors: undefined,
        imports: undefined,
        orderLines: _.map(orderToUse.data.orderLines, ol => ({
          id: ol.id,
          orderId: ol.orderId,
          price: ol.price,
          totalPrice: ol.totalPrice,
          productId: ol.productId,
          quantity: ol.quantity,
          totalVatValue: ol.totalVatValue,
          vatPercentage: ol.vatPercentage,
          substituteOrderLineId: ol.substituteOrderLineId,
          productSubstitutionId: ol.productSubstitutionId,
          externalId: ol.externalId
        }))
      });
    }
  }, [ orderToUse.data ]);

  const resyncOrder = React.useCallback(() => {
    if (orderToUse.data && orderToUse.data.orderReference) {
      importRJLOrderReq({ orderReference: orderToUse.data.orderReference });
    }
  }, [ orderToUse.data ]);

  React.useEffect(() => {
    if (importRJLOrderRes.data && orderToUse) {
      if (importRJLOrderRes.data.status === "success") {
        toast.success("Order imported successfully");
      } else {
        toast.error(`The order failed to import: ${importRJLOrderRes.data.statusMessage}`);
      }

      importRJLOrderRes.data = null;

      if (order) {
        triggerReload();
      } else if (orderId) {
        getOrder<number>(orderId);
      }
    } else if (importRJLOrderRes.error) {
      toast.error(`Could not resync. ${importRJLOrderRes.error.message}`);
      importRJLOrderRes.error = null;
    }
  }, [
    orderToUse,
    getOrder,
    importRJLOrderRes,
    order,
    orderId,
    triggerReload
  ]);

  React.useEffect(() => {
    if (submitOrderResponse.error) {
      toast.error(submitOrderResponse.error.message || "There was an error whilst submitting your order. Please try again.");
      submitOrderResponse.error = null;
    } else if (submitOrderResponse.data && orderToUse.data) {
      setOrder({
        data: {
          ...orderToUse.data,
          orderStatus: submitOrderResponse.data.orderStatus
        },
        mode: "view",
        saved: true
      });

      if (currentOrder.data && drop && role) {
        history.push(orderPlacedRedirectPath(currentOrder.data.id, drop, role, preauthEnabled));
      }

      submitOrderResponse.data = null;
    }
  }, [
    orderToUse.data,
    history,
    setOrder,
    submitOrderResponse,
    role,
    currentOrder.data,
    preauthEnabled
  ]);

  const duplicateOrder = React.useCallback((deliveryDate: Date, editBeforePlacing: boolean, customerReference?: string) => {
    if (orderToUse.data) {
      duplicateOrderReq<DuplicateOrderParams>({
        orderId: orderToUse.data.id,
        deliveryDate,
        customerReference,
        editBeforePlacing
      });
    }
  }, [ orderToUse.data ]);

  // handle create order api response
  React.useEffect(() => {
    if (duplicateOrderRes.data) {
      const isDraft = duplicateOrderRes.data.orderStatusId === _.find(orderStatuses, status => status.settings.group === "DRAFT")?.id;

      if (isDraft) {
        history.push(`/orders/${duplicateOrderRes.data.id}`, { edit: true });
        toast.warn("The order is currently in draft mode and will need to be placed for the order to be fulfilled.");
      } else if (duplicateOrderRes.data.id && drop && role) {
        history.push(orderPlacedRedirectPath(duplicateOrderRes.data.id, drop, role, preauthEnabled));
      }

      duplicateOrderRes.data = null;
    } else if (duplicateOrderRes.error) {
      toast.error(duplicateOrderRes.error.message);
      // clear the error
      duplicateOrderRes.error = null;
    }
  }, [
    drop,
    duplicateOrderRes,
    history,
    orderStatuses,
    role,
    preauthEnabled
  ]);

  return [
    {
      currentOrder: orderToUse,
      isCreatingOrder: createOrderResponse.pending,
      isSubmittingOrder: submitOrderResponse.pending,
      isSavingOrder: saveOrderResponse.pending,
      isLoading: getOrderResponse.pending,
      drop,
      cancelOrderResponse
    },
    {
      createOrder,
      setOrderMode,
      submitOrder,
      saveOrder,
      markAsManuallyProcessed,
      refreshOrder,
      cancelOrder,
      resyncOrder,
      createListFromOrder,
      setOrder,
      duplicateOrder
    }
  ];
};

export default useOrder;
